import React, { FC, useEffect, useState } from "react";
import { Button, Input, Row, Space } from "antd";
import debounce from "lodash/debounce";
import { TablePaginationConfig } from 'antd/es/table/interface';

import useListData, { LoadListData } from 'hooks/useListData';
import { ICity } from 'types/dictionaries.types';
import { IServiceItem, IServiceParams } from 'types/recommendations.types';
import { RecommendationsTable } from '../RecommendationsTable';
import { RecommendationsSelectedItems } from '../RecommendationsSelectedItems';

type AllRecommendsProps = {
  onSubmit: (items: IServiceItem[]) => Promise<any>;
  onLoad: LoadListData<IServiceItem, IServiceParams>;
  cityIds: (ICity['id'])[];
  submitting: boolean;
}

const getRowKey = (item: IServiceItem) => item.id + '/' + item.city.id;
const PAGE_SIZE = 10;

export const AllRecommends: FC<AllRecommendsProps> = ({
  onLoad,
  onSubmit,
  submitting,
  cityIds
}) => {
  const [selectedItems, setSelectedItems] = useState<IServiceItem[]>([]);
  const [search, setSearch] = useState<string>('');

  const {
    items,
    loading,
    pagination,
    onTableChange,
    onFiltersChange,
  } = useListData<IServiceItem, IServiceParams>(onLoad, {
    defaultFilters: {
      cityIds,
      q: ''
    },
    defaultPageSize: PAGE_SIZE,
    preventFirstLoad: true,
  });

  useEffect(() => {
    onFiltersChange({ cityIds, q: search });
  }, [cityIds, search]);

  const handleSubmit = () => {
    return onSubmit(selectedItems);
  };

  const handleSearchChange = debounce((e) => {
    setSearch(e.target.value);
  }, 600);

  const handleRemoveItem = (item: IServiceItem) => {
    setSelectedItems(items => items.filter(({
      id,
      city
    }) => !(id === item.id && city.id === item.city.id)));
  };
  const handleSelectItem = (item: IServiceItem, select: boolean) => {
    if (select) {
      setSelectedItems(items => [...items, item]);
    } else {
      handleRemoveItem(item);
    }
  };

  const handleTableChange = (pagination: TablePaginationConfig, filters: any) => {
    return onTableChange({
      ...pagination, pageSize: PAGE_SIZE
    }, filters);
  };

  return (
    <Space direction="vertical" style={{ width: "100%" }}>
      <RecommendationsSelectedItems
        getRowKey={getRowKey} items={selectedItems} onRemove={handleRemoveItem}
      />
      <Input
        style={{ width: '100%' }}
        placeholder="Search"
        allowClear
        onChange={handleSearchChange}
      />
      <RecommendationsTable
        rowKey={getRowKey}
        items={items}
        loading={loading}
        // kludge for wrong backend pagination, items might be more than pageSize because of splitting the services for each city
        pagination={{
          current: pagination.current,
          total: pagination.total * 10,
          pageSize: PAGE_SIZE * 10,
          showSizeChanger: false,
        }}
        onChange={handleTableChange}
        rowSelection={{
          hideSelectAll: true,
          selectedRowKeys: selectedItems.map(getRowKey),
          onSelect: handleSelectItem,
        }}
      />
      <Row justify="end">
        <Button
          disabled={!selectedItems.length}
          type="primary"
          loading={submitting}
          onClick={handleSubmit}
        >
          Сохранить
        </Button>
      </Row>
    </Space>
  );
};